// -- [Pricing section - plans grid with Monthly/Yearly toggle, ribbon, feature lightbulbs]

import { useState } from 'react'
import { MarkdownLike } from '@/components/MarkdownLike'
import { Button, SuccessTickIcon } from '@/components/ui/Button'
import type { PricingConfig, PricingPlan, PricingFeatureItem } from '@/types/content'

function LightbulbIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="15"
      height="15"
      viewBox="0 0 14 14"
      fill="currentColor"
      aria-hidden
    >
      <path d="M6.7,0C4.3,0.1,2.4,2.1,2.4,4.5c0,1.1,0.3,2.1,1,2.9C4.3,8.5,4.7,9.6,4.7,11c0,0.3,0.3,0.6,0.6,0.6h3.4 c0.3,0,0.6-0.3,0.6-0.6c0-1.4,0.5-2.5,1.3-3.5c0.7-0.8,1-1.9,1-2.9C11.6,2,9.4-0.1,6.7,0L6.7,0z M9.6,6.7c-0.8,1-1.4,2.1-1.5,3.6 H5.9C5.8,8.9,5.3,7.8,4.4,6.7C3.9,6.1,3.6,5.4,3.6,4.6c0-1.7,1.4-3.2,3.2-3.3c1.9-0.1,3.5,1.4,3.5,3.4C10.4,5.4,10.1,6.1,9.6,6.7 L9.6,6.7z" />
      <path d="M8.6,12.5H5.4c-0.3,0-0.6,0.3-0.6,0.7s0.3,0.7,0.6,0.7h3.2c0.3,0,0.6-0.3,0.6-0.7C9.2,12.9,8.9,12.5,8.6,12.5z" />
    </svg>
  )
}

// -- [Question mark circle - hover shows hint tooltip; high z-index and no parent overflow so it stays visible]
function HintIcon({ hint }: { hint: string }) {
  return (
    <span className="relative shrink-0 inline-flex group">
      <span
        className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border border-gray-400 text-gray-500 text-xs font-medium hover:border-brand-orange hover:text-brand-orange cursor-help"
        aria-label={hint}
      >
        ?
      </span>
      <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-[100] hidden group-hover:block w-64 max-w-[90vw] rounded-lg bg-gray-800 px-3 py-2 text-xs text-white shadow-xl whitespace-normal text-left">
        <MarkdownLike text={hint} asBlock />
        <span className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
      </span>
    </span>
  )
}

function FeatureRow({ item, centered }: { item: PricingFeatureItem; centered?: boolean }) {
  const text = item.text.trim()
  const plusMatch = text.match(/^Everything in (.+), plus$/i)
  const isCompletelyFree = /^Completely free$/i.test(text)
  const hasHint = item.hint != null && item.hint.trim() !== ''
  return (
    <div
      className={`flex items-center gap-2 py-2 px-4 text-sm text-gray-600 even:bg-gray-50/80 ${centered ? 'justify-center text-center' : ''}`}
    >
      {item.lightbulb && (
        <span className="shrink-0 text-amber-400" title={item.hint || undefined}>
          <LightbulbIcon />
        </span>
      )}
      <span className={centered ? '' : 'flex-1 min-w-0'}>
        {isCompletelyFree && <strong><em>{text}</em></strong>}
        {!isCompletelyFree && plusMatch && (
          <>Everything in <strong>{plusMatch[1]}</strong>, <em>plus</em></>
        )}
        {!isCompletelyFree && !plusMatch && <MarkdownLike text={text} asBlock={false} />}
      </span>
      {hasHint && <HintIcon hint={item.hint!.trim()} />}
    </div>
  )
}

function PlanCard({
  plan,
  isYearly,
  onOpenSignup,
  hasSubmittedDemo,
}: {
  plan: PricingPlan
  isYearly: boolean
  onOpenSignup: (source: string) => void
  hasSubmittedDemo: boolean
}) {
  const y = plan.yearly
  const useYearly = isYearly && y
  const subtitle = useYearly && y.subtitle != null ? y.subtitle : plan.subtitle
  const priceValue = useYearly && y.priceValue != null ? y.priceValue : plan.priceValue
  const pricePostfix = useYearly && y.pricePostfix != null ? y.pricePostfix : plan.pricePostfix
  const priceCaption = useYearly && y.priceCaption != null ? y.priceCaption : plan.priceCaption
  const buttonText = useYearly && y.buttonText != null ? y.buttonText : plan.buttonText
  const sourceLabel = `Pricing - ${plan.name} - ${buttonText}${useYearly ? ' (Yearly)' : ''}`
  const isPopular = plan.popular === true
  const isFilledButton = isPopular && !useYearly && !hasSubmittedDemo
  const hasPostfix = pricePostfix != null && pricePostfix.length > 0

  return (
    <div
      className={`relative rounded-2xl bg-white border border-gray-200 overflow-visible flex flex-col ${
        isPopular ? 'shadow-lg ring-1 ring-gray-200/80' : 'shadow-sm'
      }`}
    >
      {isPopular && (
        <div className="absolute top-0 right-0 z-10">
          <span className="block bg-brand-orange text-white text-xs font-semibold px-4 py-1 rounded-bl-lg shadow-sm">
            Popular
          </span>
        </div>
      )}
      <div className="p-8 pb-4 text-center">
        <h3 className="text-2xl font-bold text-gray-800">{plan.name}</h3>
        <div className="mt-1 text-sm text-gray-600">
          <MarkdownLike text={subtitle ?? ''} asBlock />
        </div>
      </div>
      <div className="px-8 py-4 text-center">
        <div className="flex items-baseline justify-center gap-0.5">
          <sup className="text-sm font-bold text-gray-700">{plan.priceCurrency}</sup>
          <span className="text-2xl font-bold text-gray-800">{priceValue}</span>
          {hasPostfix && (
            <span className="text-gray-600 text-sm ml-1">
              /&nbsp;{pricePostfix}
            </span>
          )}
        </div>
        {priceCaption != null && priceCaption !== '' && (
          <div className="mt-1 text-sm text-gray-500">
            <MarkdownLike text={priceCaption} asBlock />
          </div>
        )}
      </div>
      <div className="px-8 py-4 text-center">
        <Button
          variant={
            hasSubmittedDemo ? 'success' : isFilledButton ? 'primary' : 'secondary'
          }
          onClick={() => onOpenSignup(sourceLabel)}
          className="w-full text-sm"
          data-umami-event={`Pricing - ${plan.name}${useYearly ? ' (Yearly)' : ''}`}
        >
          {hasSubmittedDemo && <SuccessTickIcon className="h-4 w-4 shrink-0" />}
          {buttonText}
        </Button>
        {!useYearly && plan.buttonCaption && (
          <div className="mt-2 text-xs text-gray-500">
            <MarkdownLike text={plan.buttonCaption} asBlock />
          </div>
        )}
      </div>
      <div className="flex-1 border-t border-gray-100">
        {plan.features.map((item, i) => (
          <FeatureRow key={i} item={item} centered />
        ))}
      </div>
    </div>
  )
}

interface PricingSectionProps {
  pricing: PricingConfig
  onOpenSignup: (source: string) => void
  /** -- When true, plan buttons show green success + tick (already submitted) */
  hasSubmittedDemo?: boolean
}

export function PricingSection({ pricing, onOpenSignup, hasSubmittedDemo = false }: PricingSectionProps) {
  const [isYearly, setIsYearly] = useState(false)
  const { sectionTitle, yearlyHint, plans } = pricing
  if (!plans || plans.length === 0) return null

  // -- When Yearly is selected, only show plans that are available for yearly billing
  const displayPlans = isYearly
    ? plans.filter((p) => p.yearlyAvailable === true)
    : plans

  return (
    <section id="pricing" className="py-16 px-6 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        {sectionTitle && (
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-2">
            <MarkdownLike text={sectionTitle} asBlock={false} />
          </h2>
        )}
        {yearlyHint && (
          <div className="flex justify-center mb-10">
            <div className="inline-flex rounded-lg border border-brand-orange/30 bg-white p-0.5">
              <button
                type="button"
                onClick={() => setIsYearly(false)}
                className={`min-h-12 px-5 py-3 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  !isYearly
                    ? 'bg-brand-orange/10 text-brand-orange border border-brand-orange/30'
                    : 'text-brand-orange hover:bg-gray-50'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setIsYearly(true)}
                className="relative min-h-12 px-5 py-3 rounded-md text-sm font-medium transition-colors text-brand-orange hover:bg-gray-50 cursor-pointer"
              >
                {yearlyHint && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-brand-yellow text-gray-900 text-xs font-semibold px-2 py-0.5 rounded whitespace-nowrap shadow-sm">
                    <MarkdownLike text={yearlyHint} asBlock={false} />
                  </span>
                )}
                Yearly
              </button>
            </div>
          </div>
        )}
        <div
          className={`grid grid-cols-1 gap-6 ${
            displayPlans.length === 1
              ? 'max-w-md mx-auto'
              : 'md:grid-cols-2 xl:grid-cols-4'
          }`}
        >
          {displayPlans.map((plan) => (
            <PlanCard
              key={plan.name}
              plan={plan}
              isYearly={isYearly}
              onOpenSignup={onOpenSignup}
              hasSubmittedDemo={hasSubmittedDemo}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
